/**
 * "Never change recorded history": whether an attached photo is evidence
 * filed after its pour closed.
 *
 * This is a single pure predicate so the live PhotoField tile, the server
 * PDF, the print/save-as-PDF HTML, and the CSV export can never disagree
 * about which photos are marked — the same failure mode `../calc`'s own
 * header warns about for the arithmetic checks applies here too.
 *
 * Fix-round C1: the predicate compares `taken_at` (stamped on the device the
 * instant the shutter fires — src/lib/photo.ts's `enqueuePhoto`), falling
 * back to `created_at` (stamped by the server at attach time) only when the
 * device did not supply one. `created_at` alone is the WRONG timestamp: the
 * queue is explicitly designed to let a photo land long after it was taken —
 * a bad-signal retry, or the entire backlog stranded while R2 was off — and
 * none of that delay is evidence of when the photo was actually taken. Using
 * `created_at` would falsely mark every stranded photo, taken mid-pour but
 * uploaded after close, as "added after close": exactly the false statement
 * about provenance this feature exists to prevent.
 */
import type { Photo, Timestamp } from '../types';

/**
 * True when `photo` was taken (or, lacking that, attached) after `closedAt`.
 * `closedAt` is the pour's `closed_at` — `null` for an open pour, which is
 * never "after close".
 *
 * Both sides are ISO 8601 timestamps, which sort lexically the same as
 * chronologically, so a plain string comparison is safe.
 */
export function photoAddedAfterClose(
  photo: Pick<Photo, 'taken_at' | 'created_at'>,
  closedAt: Timestamp | null
): boolean {
  if (closedAt === null) return false;
  return (photo.taken_at ?? photo.created_at) > closedAt;
}
